import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

// Criação de grupo em background leva 30-90s (reconciliação + create + configs).
// Com o grupo reserva (services/rotacaoReserva.js) esse caminho é raro, mas quando
// cai nele o usuário precisa de folga suficiente pra não ver "demorou demais".
const MAX_RETRIES = 6;

export default function GrupoRedirect() {
    const { slug } = useParams();
    const [status, setStatus] = useState("loading"); // loading, found, error, creating, rate_limited
    const [grupoInfo, setGrupoInfo] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [retryCountdown, setRetryCountdown] = useState(0);

    // Refs para limpar timers em unmount e evitar leaks entre retries
    const tickIntervalRef = useRef(null);
    const retryTimeoutRef = useRef(null);

    useEffect(() => {
        const clearTimers = () => {
            if (tickIntervalRef.current) {
                clearInterval(tickIntervalRef.current);
                tickIntervalRef.current = null;
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };

        const buscarGrupo = async (attempt = 0) => {
            try {
                const res = await fetch(`/api/g/${slug}`);
                let data = {};
                try {
                    data = await res.json();
                } catch {
                    data = {};
                }

                if (res.status === 503) {
                    if (attempt >= MAX_RETRIES) {
                        setErrorMsg("Demorou mais que o esperado. Tente recarregar a página.");
                        setStatus("error");
                        return;
                    }
                    const retryAfter = Number(data.retryAfter) > 0 ? Number(data.retryAfter) : 10;
                    setRetryCountdown(retryAfter);
                    setStatus("creating");

                    clearTimers();

                    // Countdown visual
                    tickIntervalRef.current = setInterval(() => {
                        setRetryCountdown(prev => {
                            if (prev <= 1) {
                                if (tickIntervalRef.current) {
                                    clearInterval(tickIntervalRef.current);
                                    tickIntervalRef.current = null;
                                }
                                return 0;
                            }
                            return prev - 1;
                        });
                    }, 1000);

                    // Retry após retryAfter segundos
                    retryTimeoutRef.current = setTimeout(() => {
                        if (tickIntervalRef.current) {
                            clearInterval(tickIntervalRef.current);
                            tickIntervalRef.current = null;
                        }
                        retryTimeoutRef.current = null;
                        buscarGrupo(attempt + 1);
                    }, retryAfter * 1000);
                    return;
                }

                if (res.status === 429) {
                    setErrorMsg(data.error || "Muitos acessos. Aguarde um minuto e recarregue.");
                    setStatus("rate_limited");
                    return;
                }

                if (!res.ok) {
                    setErrorMsg(data.error || "Erro ao localizar grupo");
                    setStatus("error");
                    return;
                }

                setGrupoInfo(data);
                setStatus("found");
                document.title = data.campanhaTitulo || data.nomeGrupo;

                // Redireciona após 1.5 segundos
                setTimeout(() => {
                    try {
                        const a = document.createElement("a");
                        a.href = data.inviteLink;
                        a.target = "_top"; // breakout of webviews
                        document.body.appendChild(a);
                        a.click();
                    } catch (e) {
                        window.location.replace(data.inviteLink);
                    }
                }, 1500);
            } catch {
                setErrorMsg("Erro de conexão");
                setStatus("error");
            }
        };

        buscarGrupo(0);

        return () => {
            clearTimers();
        };
    }, [slug]);

    return (
        <div style={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #0a7e8c 0%, #1a5276 50%, #154360 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
            padding: "20px",
        }}>
            <div style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "48px 40px",
                maxWidth: "420px",
                width: "100%",
                textAlign: "center",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}>
                {status === "loading" && (
                    <>
                        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a", marginBottom: "12px" }}>
                            Localizando grupo...
                        </h1>
                        <p style={{ color: "#666", fontSize: "15px", marginBottom: "24px" }}>
                            Por favor, aguarde alguns segundos.
                        </p>
                        <div style={{
                            width: "40px", height: "40px", margin: "0 auto",
                            border: "3px solid #e0e0e0", borderTop: "3px solid #0a7e8c",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                        }} />
                    </>
                )}

                {status === "creating" && (
                    <>
                        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a", marginBottom: "12px" }}>
                            Preparando seu grupo
                        </h1>
                        <p style={{ color: "#666", fontSize: "15px", marginBottom: "24px" }}>
                            Estamos abrindo um novo grupo agora. Você vai entrar em {retryCountdown}s...
                        </p>
                        <div style={{
                            width: "40px", height: "40px", margin: "0 auto",
                            border: "3px solid #e0e0e0", borderTop: "3px solid #25D366",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                        }} />
                    </>
                )}

                {status === "rate_limited" && (
                    <>
                        <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏱️</div>
                        <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#1a1a1a", marginBottom: "8px" }}>
                            Aguarde um pouco
                        </h1>
                        <p style={{ color: "#666", fontSize: "15px" }}>
                            {errorMsg}
                        </p>
                    </>
                )}

                {status === "found" && grupoInfo && (
                    <>
                        {grupoInfo.fotoUrl && (
                            <img
                                src={grupoInfo.fotoUrl}
                                alt=""
                                style={{
                                    width: "80px", height: "80px", borderRadius: "50%",
                                    objectFit: "cover", margin: "0 auto 16px", display: "block",
                                    border: "3px solid #e0e0e0",
                                }}
                            />
                        )}
                        <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#1a1a1a", marginBottom: "8px" }}>
                            Grupo encontrado!
                        </h1>
                        <p style={{ color: "#333", fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>
                            {grupoInfo.nomeGrupo}
                        </p>
                        <p style={{ color: "#666", fontSize: "14px", marginBottom: "24px" }}>
                            Redirecionando para o WhatsApp...
                        </p>
                        <div style={{
                            width: "40px", height: "40px", margin: "0 auto",
                            border: "3px solid #e0e0e0", borderTop: "3px solid #25D366",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                        }} />
                        <a
                            href={grupoInfo.inviteLink}
                            style={{
                                display: "inline-block", marginTop: "20px",
                                background: "#25D366", color: "#fff", padding: "12px 32px",
                                borderRadius: "8px", textDecoration: "none", fontWeight: 600,
                                fontSize: "15px",
                            }}
                        >
                            Entrar no grupo
                        </a>
                    </>
                )}

                {status === "error" && (
                    <>
                        <div style={{ fontSize: "48px", marginBottom: "16px" }}>😔</div>
                        <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#1a1a1a", marginBottom: "8px" }}>
                            Ops!
                        </h1>
                        <p style={{ color: "#666", fontSize: "15px" }}>
                            {errorMsg}
                        </p>
                    </>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
